// NOI Studio, "A · Monocrom". Behaviour spec: docs/redesign/README.md §4 and §6.
import "./main.css";

import Alpine from "alpinejs";
import htmx from "htmx.org";

window.Alpine = Alpine;
window.htmx = htmx;

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), textarea, select, [tabindex]:not([tabindex="-1"])';

// Keep Tab and Shift+Tab inside `container`.
function trapFocus(event, container) {
  if (event.key !== "Tab") return;
  const nodes = [...container.querySelectorAll(FOCUSABLE)].filter(
    (node) => node.offsetParent !== null,
  );
  if (!nodes.length) return;
  const first = nodes[0];
  const last = nodes[nodes.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

// While the menu or a gallery is open, everything else is inert: no focus, hidden from screen readers.
function isolate(element, on) {
  if (!on) {
    document.querySelectorAll("[data-isolated]").forEach((node) => {
      node.inert = false;
      delete node.dataset.isolated;
    });
    return;
  }
  for (let node = element; node?.parentElement && node !== document.body; node = node.parentElement) {
    for (const sibling of node.parentElement.children) {
      if (sibling === node || sibling.inert || ["SCRIPT", "TEMPLATE", "STYLE"].includes(sibling.tagName)) continue;
      sibling.inert = true;
      sibling.dataset.isolated = "";
    }
  }
}

// Galleries: a swipe is one finger. Two fingers zoom, and once the page is zoomed in one finger
// pans the photo, so neither moves to another photo.
function swipeStart(event) {
  return event.touches.length === 1 ? event.changedTouches[0].clientX : null;
}

function swipeDirection(startX, event) {
  if (startX === null || event.touches.length) return 0;
  if ((window.visualViewport?.scale || 1) > 1.01) return 0;
  const dx = event.changedTouches[0].clientX - startX;
  return Math.abs(dx) < 40 ? 0 : dx < 0 ? 1 : -1;
}

// Dots under a photo, Instagram style, instead of "3 / 12": at most five; on a longer set the
// row slides along with the photo and the end dots shrink while there are more photos past them.
function dotWindow(current, total, max = 5) {
  if (total < 2 || current < 0) return [];
  const start = Math.max(0, Math.min(current - Math.floor(max / 2), total - max));
  const end = Math.min(total, start + max);
  return Array.from({ length: end - start }, (_, k) => {
    const i = start + k;
    return { i, active: i === current, small: (i === start && start > 0) || (i === end - 1 && end < total) };
  });
}

let scrollLocks = 0;
function lockScroll(lock) {
  scrollLocks = Math.max(0, scrollLocks + (lock ? 1 : -1));
  document.documentElement.style.overflow = scrollLocks ? "hidden" : "";
}

// Header: follows the scroll pixel for pixel. Scrolling down pushes it up (the sticky
// filter bar under it goes too), scrolling up brings it back, and it stays wherever the
// scrolling stops. Fully shown at the top of the page. CSS reads --hdr-shift (main.css).
Alpine.data("siteHeader", () => ({
  lastY: 0,
  shift: 0,
  range: 0,
  init() {
    let ticking = false;
    this.lastY = Math.max(0, window.scrollY);
    this.measure();
    window.addEventListener("resize", () => this.measure(), { passive: true });
    window.addEventListener(
      "scroll",
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          this.update();
          ticking = false;
        });
      },
      { passive: true },
    );
  },
  // how far it can go: the header plus the filter bar under it, if the page has one
  measure() {
    const bar = document.querySelector(".fbar");
    this.range = this.$el.offsetHeight + (bar ? bar.offsetHeight : 0);
  },
  update() {
    const y = Math.max(0, window.scrollY);
    if (!scrollLocks) {
      this.shift = Math.min(Math.max(0, this.shift + y - this.lastY), this.range, y);
      document.documentElement.style.setProperty("--hdr-shift", `${Math.round(this.shift)}px`);
    }
    this.lastY = y;
  },
}));

// Side menu: slides in from the right; Esc, scrim click, focus trap, scroll lock.
Alpine.data("siteMenu", () => ({
  open: false,
  returnFocus: null,
  toggle() {
    this.open ? this.close() : this.show();
  },
  show() {
    this.open = true;
    this.returnFocus = document.activeElement;
    lockScroll(true);
    isolate(this.$refs.panel, true);
    this.$nextTick(() => this.$refs.close.focus());
  },
  close() {
    if (!this.open) return;
    this.open = false;
    lockScroll(false);
    isolate(null, false);
    this.returnFocus?.focus();
  },
  trap(event) {
    trapFocus(event, this.$refs.panel);
  },
}));

// Lightbox: every img[data-lightbox] in the page, in page order, without
// duplicates (D12). Arrows wrap around; ←/→/Esc and swipe work.
Alpine.data("lightbox", () => ({
  items: [],
  index: 0,
  open: false,
  touchX: null,
  returnFocus: null,
  // Experiment: on a project page the gallery ends with a slide announcing the next project.
  neighbour(kind) {
    const data = this.$root.dataset;
    const url = data[`${kind}Url`];
    if (!url) return null;
    return { type: kind, url, title: data[`${kind}Title`], meta: data[`${kind}Meta`] || "", cover: data[`${kind}Cover`] || "" };
  },
  collect() {
    const seen = new Set();
    const images = [...this.$root.querySelectorAll("img[data-lightbox]")]
      .map((img) => ({ type: "image", src: img.dataset.lightbox, alt: img.dataset.caption || img.alt || "" }))
      .filter((item) => item.src && !seen.has(item.src) && seen.add(item.src));
    const next = this.neighbour("next");
    this.items = [...images, ...(next ? [next] : [])];
  },
  images() {
    return this.items.filter((item) => item.type === "image");
  },
  show(index) {
    this.index = index;
    this.open = true;
    lockScroll(true);
    isolate(this.$refs.dialog, true);
    this.$nextTick(() => this.$refs.closeButton.focus());
  },
  openFrom(event) {
    const trigger = event.target.closest("[data-lightbox-trigger]");
    if (!trigger) return;
    const img = trigger.querySelector("img[data-lightbox]");
    if (!img) return;
    event.preventDefault();
    this.collect();
    this.returnFocus = trigger;
    this.show(Math.max(0, this.items.findIndex((item) => item.src === img.dataset.lightbox)));
  },
  close() {
    if (!this.open) return;
    this.open = false;
    lockScroll(false);
    isolate(null, false);
    this.returnFocus?.focus?.();
  },
  // With a "next project" slide the gallery runs from the first photo to that slide and stops
  // there; without one (articles) the arrows wrap around.
  get linear() {
    return this.items[this.items.length - 1]?.type === "next";
  },
  get canNext() {
    return !this.linear || this.index < this.items.length - 1;
  },
  get canPrev() {
    return !this.linear || this.index > 0;
  },
  next() {
    if (!this.canNext) return;
    this.index = (this.index + 1) % this.items.length;
    // On the last slide the next arrow goes away; if it had the focus, keep the focus in the
    // dialog itself (no outline) rather than drawing a frame around the whole slide.
    this.$nextTick(() => {
      const active = document.activeElement;
      if (!this.$refs.dialog.contains(active) || getComputedStyle(active).visibility === "hidden") this.$refs.dialog.focus();
    });
  },
  prev() {
    if (!this.canPrev) return;
    this.index = (this.index - 1 + this.items.length) % this.items.length;
  },
  get current() {
    return this.items[this.index] || { type: "image", src: "", alt: "" };
  },
  // Dots for the photos (none on the "next project" slide); the position in words for screen readers.
  get dots() {
    const photos = this.images();
    return dotWindow(photos.indexOf(this.current), photos.length);
  },
  position(template) {
    const photos = this.images();
    const i = photos.indexOf(this.current);
    return i < 0 ? "" : template.replace("{n}", i + 1).replace("{total}", photos.length);
  },
  // Caption cue on the last photo: what the next arrow leads to.
  get upcoming() {
    const following = this.items[this.index + 1];
    return this.current.type === "image" && following && following.type === "next" ? following.title : "";
  },
  keydown(event) {
    if (!this.open) return;
    if (event.key === "Escape") this.close();
    else if (event.key === "ArrowRight") this.next();
    else if (event.key === "ArrowLeft") this.prev();
    else trapFocus(event, this.$refs.dialog);
  },
  touchStart(event) {
    this.touchX = swipeStart(event);
  },
  touchEnd(event) {
    const direction = swipeDirection(this.touchX, event);
    if (!event.touches.length) this.touchX = null;
    if (direction > 0) this.next();
    else if (direction < 0) this.prev();
  },
}));

const prefersReducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// The projects list as a feed on phones (portrait, or turned sideways; main.css).
const phoneFeed = () => window.matchMedia("(max-width: 899px), (orientation: landscape) and (max-height: 500px)").matches;

// Turning the phone keeps the project you were looking at on screen: the one with the most of
// its photo visible, tracked while scrolling.
let feedCard = null;
let feedTicking = false;
function trackFeedCard() {
  let best = null;
  let bestVisible = 0;
  document.querySelectorAll("[data-carousel-card] .carousel-frame").forEach((frame) => {
    const box = frame.getBoundingClientRect();
    const visible = Math.min(box.bottom, window.innerHeight) - Math.max(box.top, 0);
    if (visible > bestVisible) {
      best = frame;
      bestVisible = visible;
    }
  });
  feedCard = best;
}
window.addEventListener(
  "scroll",
  () => {
    if (feedTicking) return;
    feedTicking = true;
    requestAnimationFrame(() => {
      trackFeedCard();
      feedTicking = false;
    });
  },
  { passive: true },
);
window.matchMedia("(orientation: landscape)").addEventListener("change", () => {
  const frame = feedCard;
  if (!frame?.isConnected || !phoneFeed()) return;
  // after the new layout: sideways the photo fills the screen, upright it sits under the header
  setTimeout(() => frame.scrollIntoView({ block: "start", behavior: "auto" }), 200);
});

// Experiment: a project card as a carousel of all its photos (list page).
Alpine.data("cardCarousel", (total = 1) => ({
  total,
  index: 0,
  step() {
    const slides = this.$refs.track.children;
    return slides.length > 1 ? slides[1].offsetLeft - slides[0].offsetLeft : this.$refs.track.clientWidth;
  },
  current() {
    return Math.min(this.index, this.total - 1);
  },
  get dots() {
    return dotWindow(this.current(), this.total);
  },
  onScroll() {
    this.index = Math.round(this.$refs.track.scrollLeft / this.step());
  },
  goTo(position, instant = false) {
    this.$refs.track.scrollTo({ left: position * this.step(), behavior: instant || prefersReducedMotion() ? "auto" : "smooth" });
  },
  // The photos go round: after the last comes the first, before the first the last. Going
  // round is a jump, not a scroll back through every photo.
  next() {
    if (this.index >= this.total - 1) this.goTo(0, true);
    else this.goTo(this.index + 1);
  },
  prev() {
    if (this.index <= 0) this.goTo(this.total - 1, true);
    else this.goTo(this.index - 1);
  },
  // Phones: the browser's own swiping stops at either end; a swipe past an end goes round.
  touchStart(event) {
    const touch = event.touches.length === 1 && (window.visualViewport?.scale || 1) <= 1.01 ? event.touches[0] : null;
    this.swipe = touch && { x: touch.clientX, y: touch.clientY, first: this.index <= 0, last: this.index >= this.total - 1 };
  },
  touchEnd(event) {
    const swipe = this.swipe;
    this.swipe = null;
    if (!swipe || event.touches.length || this.total < 2) return;
    const dx = event.changedTouches[0].clientX - swipe.x;
    const dy = event.changedTouches[0].clientY - swipe.y;
    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    if (swipe.first && dx > 0) this.goTo(this.total - 1, true);
    else if (swipe.last && dx < 0) this.goTo(0, true);
  },
  // Desktop: the photos can be dragged with the mouse; a click without dragging still opens
  // the project. Snapping is off while dragging, then the photo settles on the nearest one
  // in the direction of the drag (going round at the ends).
  dragStart(event) {
    if (event.pointerType !== "mouse" || event.button !== 0 || this.total < 2) return;
    const track = this.$refs.track;
    const drag = { x: event.clientX, left: track.scrollLeft, index: this.index, moved: false };
    const move = (e) => {
      const dx = e.clientX - drag.x;
      if (!drag.moved && Math.abs(dx) > 5) {
        drag.moved = true;
        track.classList.add("is-dragging");
      }
      if (drag.moved) track.scrollLeft = drag.left - dx;
    };
    const up = (e) => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      if (!drag.moved) return;
      this.dragged = true;
      const dx = e.clientX - drag.x;
      this.index = drag.index;
      if (dx < -40) this.next();
      else if (dx > 40) this.prev();
      else this.goTo(drag.index);
      setTimeout(() => track.classList.remove("is-dragging"), 450);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  },
  // the click that ends a drag must not open the project
  swallowClick(event) {
    if (!this.dragged) return;
    this.dragged = false;
    event.preventDefault();
    event.stopPropagation();
  },
  // Turning the phone changes the photo width: stay on the same photo.
  width: window.innerWidth,
  keepPhoto() {
    if (window.innerWidth === this.width) return;
    this.width = window.innerWidth;
    this.$refs.track.scrollTo({ left: this.index * this.step(), behavior: "auto" });
  },
}));

// Video block: poster first, the iframe only after a click (lite embed).
Alpine.data("videoEmbed", () => ({
  playing: false,
  play() {
    this.playing = true;
  },
}));

// Email / phone kept out of the HTML: it travels reversed and base64-encoded (encode_contact).
function reveal(encoded) {
  try {
    const bytes = Uint8Array.from(atob(encoded), (c) => c.charCodeAt(0));
    return [...new TextDecoder().decode(bytes)].reverse().join("");
  } catch {
    return "";
  }
}

Alpine.data("protectedContact", (kind, encoded) => ({
  value: "",
  init() {
    this.value = reveal(encoded);
  },
  get href() {
    if (!this.value) return null;
    return kind === "email" ? `mailto:${this.value}` : `tel:${this.value.replace(/[^+\d]/g, "")}`;
  },
}));

// Contact form: a valid submission opens the visitor's email app with the message filled in.
// HTMX still posts the form, so the server validates it and keeps a copy in the admin.
Alpine.data("contactMailto", (encoded) => ({
  compose(event) {
    const url = this.mailtoUrl(event.target);
    if (url) window.location.href = url;
  },
  mailtoUrl(form) {
    const to = reveal(encoded);
    if (!to || !form.checkValidity()) return "";
    const lines = [];
    let name = "";
    form.querySelectorAll(".field").forEach((field) => {
      const input = field.querySelector("input, textarea, select");
      const label = field.querySelector("label")?.textContent.replace("*", "").trim();
      if (!input || !input.value.trim()) return;
      if (!name && input.getAttribute("autocomplete") === "name") name = input.value.trim();
      lines.push(input.tagName === "TEXTAREA" ? `\n${input.value.trim()}` : `${label}: ${input.value.trim()}`);
    });
    const subject = `${document.documentElement.lang === "en" ? "Message from the website" : "Mesaj de pe site"}${name ? ` · ${name}` : ""}`;
    return `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.join("\n"))}`;
  },
}));

// Contact form: after an HTMX swap, move focus to the first error or to the
// thank-you message, so keyboard and screen reader users land on the result.
document.addEventListener("htmx:afterSettle", (event) => {
  // Filter bar: say how many results the new list has (the status sits outside the list).
  if (event.detail.elt?.closest?.(".fbar")) {
    const status = document.getElementById("filter-status");
    const list = document.getElementById("filtered-list");
    if (status && list) status.textContent = list.dataset.status || "";
    return;
  }
  const slot = document.querySelector("[data-focus-after-swap]");
  (slot?.querySelector("[aria-invalid='true']") || slot?.querySelector("[data-focus-first]"))?.focus();
});

Alpine.start();
